import { categoryType, useCreateAiWorkersMutation, RouteState, useUpdateAiWorkersMutation, useTypedSelector, useGetDifyWorkflowsQuery, useGetCredentialsListQuery } from '@circuitry-ai/doc-data';
import { checkType, CtyButton, CtyShowToast, CopyCodeField, optionalCodePayload } from '@circuitry-ai/doc-ui';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Select, SelectItem, Textarea } from '@nextui-org/react';

const LABEL_CLASS = 'text-[#414651] font-[Poppins] font-medium text-sm leading-[20px] tracking-normal';
const DESC_CLASS = 'font-[Inter] font-normal text-sm leading-5 tracking-normal text-[#535862]';

interface FormRowProps {
    label: string;
    htmlFor?: string;
    required?: boolean;
    description?: string;
    children: ReactNode;
}

export interface UXGenieConfigurationProps {
    /** When inside Settings, use this path for post-save navigation (e.g. /settings/uxgenie) */
    basePath?: string;
}

const FormRow = ({ label, htmlFor, required, description, children }: FormRowProps) => (
    <div className="flex flex-row gap-3">
        <label htmlFor={htmlFor} className="flex flex-col w-[30%]">
            <span className={LABEL_CLASS}>
                {label}{required && <span className="text-danger">*</span>}
            </span>
            {description && <div className={DESC_CLASS}>{description}</div>}
        </label>
        <div className="flex-1">{children}</div>
    </div>
);

export interface UXGenieConfigurationProps { }

export function UXGenieConfiguration(props: UXGenieConfigurationProps) {

    const { basePath = '/uxgenie' } = props;
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    const token = useTypedSelector((state) => state.auth.token);

    const { mode } = useParams<{ mode: string; }>();
    const [createAiWorkers, { isLoading: isUXGenieCreationLoading }] = useCreateAiWorkersMutation();
    const [updateAiWorkers, { isLoading: isUXGenieUpdateLoading }] = useUpdateAiWorkersMutation();

    const { uxgenieWorker, worker } = location?.state ?? {};
    const workerFromState = uxgenieWorker || worker;
    const isCreateMode = mode === RouteState.CREATE;

    const [workerCode, setWorkerCode] = useState<string>('');
    const [workerName, setWorkerName] = useState<string>('');
    const [publicDescription, setPublicDescription] = useState<string>('');
    const [workerSubType, setWorkerSubType] = useState<string>('');
    const [selectedWorkflowId, setSelectedWorkflowId] = useState<string>('');
    const [selectedCredentialId, setSelectedCredentialId] = useState<string>('');

    const { data: workflowsResponse, isLoading: isLoadingWorkflows } = useGetDifyWorkflowsQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
    const workflowsData = workflowsResponse || [];
    const { data: credentialsList, isLoading: isCredentialsListLoading } = useGetCredentialsListQuery(undefined, { skip: !token, refetchOnMountOrArgChange: true });
    const credentialOptions = Array.isArray(credentialsList) ? credentialsList : [];

    useEffect(() => {
        if (workerFromState) {
            const result: Record<string, any> = {};
            (workerFromState.aiworkers_attrs ?? []).forEach((item: any) => {
                result[item?.attr_name] = item.attr_json !== null ? item.attr_json : item.attr_value;
            });
            setWorkerCode(workerFromState.code ?? '');
            setWorkerName(workerFromState.name ?? '');
            setPublicDescription(workerFromState.description ?? '');
            setWorkerSubType(workerFromState.sub_type ?? '');
            if (result?.workflow_id) setSelectedWorkflowId(result.workflow_id);
            if (result?.credential_id) setSelectedCredentialId(result.credential_id);
        }
    }, [workerFromState]);

    const handleSave = async () => {
        try {
            if (!workerName.trim()) {
                CtyShowToast(checkType.error, t('Worker name is required'));
                return;
            }
            if (!selectedWorkflowId) {
                CtyShowToast(checkType.error, t('Please select a workflow'));
                return;
            }
            if (!selectedCredentialId) {
                CtyShowToast(checkType.error, t('Please select a credential'));
                return;
            }

            const aiworkersAttrs: any[] = [
                { attr_name: 'workflow_id', attr_value: selectedWorkflowId, attr_json: null },
            ];
            const selectedWorkflow = workflowsData.find((w: any) => w.workflow_id === selectedWorkflowId);
            if (selectedWorkflow?.mode != null) {
                aiworkersAttrs.push({ attr_name: 'workflow_type', attr_value: String(selectedWorkflow.mode), attr_json: null });
            }
            aiworkersAttrs.push({ attr_name: 'credential_id', attr_value: selectedCredentialId, attr_json: null });

            const payload = {
                name: workerName,
                description: publicDescription,
                sub_type: workerSubType.trim(),
                aiworkers_attrs: aiworkersAttrs,
                ...(isCreateMode ? optionalCodePayload(workerCode) : {}),
            };

            if (isCreateMode) {
                const response = await createAiWorkers({ category: categoryType.UXGENIE, type: 'product', ...payload }).unwrap();
                CtyShowToast(checkType.success, t('UXGenie Template created successfully'));
                navigate(`${basePath}/${response.data.aiworker_id}?tab=configuration`, {
                    state: { uxgenieWorker: response.data }
                });
            } else {
                if (!workerFromState?.aiworker_id) {
                    CtyShowToast(checkType.error, t('Worker ID not found'));
                    return;
                }
                const response = await updateAiWorkers({ payLoad: payload, aiworker_id: workerFromState.aiworker_id }).unwrap();
                navigate(`${location.pathname}${location.search}`, { state: { uxgenieWorker: response?.data || workerFromState } });
                CtyShowToast(checkType.success, t('UXGenie Template updated successfully'));
            }
        } catch (error: any) {
            console.error('Error saving UXGenie Template:', error);
            CtyShowToast(checkType.error, error?.data?.message || t('Failed to save UXGenie Template'));
        }
    };

    return (
        <div className='flex flex-col h-full gap-3 pt-2 pb-3'>
            <div className="flex-1 overflow-y-auto w-full px-5">
                <div className='flex flex-col gap-3'>
                    <div className="md:w-[70%] flex flex-col gap-3">
                        <CopyCodeField
                            label="Code"
                            value={workerCode}
                            layout="horizontal"
                            isReadOnly={!isCreateMode}
                            isRequired={false}
                            subText="Must be at least 8 alphanumeric characters"
                            onValueChange={isCreateMode ? setWorkerCode : undefined}
                        />
                        <FormRow label={t('Worker name')} htmlFor="worker_name" required>
                            <Input
                                id="worker_name"
                                type="text"
                                size="sm"
                                aria-label="worker_name"
                                value={workerName}
                                isRequired
                                onValueChange={setWorkerName}
                            />
                        </FormRow>

                        {!isCreateMode && (
                            <FormRow label={t('Worker ID')} htmlFor="worker_id">
                                <Input
                                    id="worker_id"
                                    type="text"
                                    size="sm"
                                    aria-label="worker_id"
                                    isReadOnly
                                    value={workerFromState?.aiworker_id ?? ''}
                                />
                            </FormRow>
                        )}

                        <FormRow label={t('Public description')} htmlFor="public_description" required description={t('Write a short description')}>
                            <Textarea
                                id="public_description"
                                minRows={5}
                                maxRows={7}
                                isRequired
                                aria-label="public_description"
                                value={publicDescription}
                                onValueChange={setPublicDescription}
                            />
                        </FormRow>

                        <FormRow label={t('SubType')} htmlFor="worker_sub_type">
                            <Input
                                id="worker_sub_type"
                                type="text"
                                size="sm"
                                aria-label="worker_sub_type"
                                value={workerSubType}
                                onValueChange={setWorkerSubType}
                            />
                        </FormRow>
                    </div>

                    <div className="md:w-[70%] flex flex-col gap-3">
                        <FormRow label={t('Select Workflow')} required description={t('Choose a workflow template')}>
                            <Select
                                id="workflow-select"
                                size="sm"
                                aria-label="Select Workflow"
                                placeholder={isLoadingWorkflows ? t('Loading workflows...') : t('Select a workflow')}
                                selectedKeys={selectedWorkflowId ? new Set([selectedWorkflowId]) : new Set()}
                                onSelectionChange={(keys) => setSelectedWorkflowId(String([...keys][0]))}
                                isLoading={isLoadingWorkflows}
                                isDisabled={isLoadingWorkflows}
                            >
                                {workflowsData.map((workflow: any) => (
                                    <SelectItem key={workflow.workflow_id} value={workflow.workflow_id}>
                                        {workflow.workflow_name}
                                    </SelectItem>
                                ))}
                            </Select>
                        </FormRow>

                        <FormRow label={t('Select Credential')} required description={t('Choose a credential for the workflow')}>
                            <Select
                                id="credential-select"
                                size="sm"
                                aria-label="Select Credential"
                                placeholder={isCredentialsListLoading ? t('Loading credentials...') : t('Select a credential')}
                                selectedKeys={selectedCredentialId ? new Set([selectedCredentialId]) : new Set()}
                                onSelectionChange={(keys) => setSelectedCredentialId(String([...keys][0] ?? ''))}
                                isLoading={isCredentialsListLoading}
                                isDisabled={isCredentialsListLoading}
                            >
                                {credentialOptions.map((credential: any) => (
                                    <SelectItem key={credential.credential_id} value={credential.credential_id}>
                                        {credential.name}
                                    </SelectItem>
                                ))}
                            </Select>
                        </FormRow>
                    </div>

                    <div className='flex justify-end md:w-[70%]'>
                        <CtyButton variant="solid" color="primary" size="md" radius="sm" onPress={handleSave} isLoading={isUXGenieCreationLoading || isUXGenieUpdateLoading}>
                            {isCreateMode ? t('Save') : t('Update')}
                        </CtyButton>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default UXGenieConfiguration;
